import { useState } from "react";

import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

import { getOrganizationClient } from "@/api";
import { IOrganization } from "@/models";
import { toast } from "./ui/use-toast";

interface OrganizationCancelDeletionDialogProps {
  organization?: IOrganization;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancelDeletion: () => void;
}

const organizationClient = getOrganizationClient();

export function OrganizationCancelDeletionDialog({
  organization,
  onOpenChange,
  open,
  onCancelDeletion,
}: OrganizationCancelDeletionDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleCancelDeletion = async () => {
    setIsLoading(true);

    try {
      await organizationClient.delete(
        `/v2/internal/organizations/cleanup/${organization?.id}`
      );

      toast({
        title: "Deletion cancelled",
        description:
          "The organization has been removed from the cleanup queue.",
      });

      onCancelDeletion();
    } catch (error: unknown) {
      const axiosError = error as { response?: { status: number }; message?: string };

      if (axiosError.response?.status === 404) {
        toast({
          title: "Already removed",
          description:
            "This organization was already removed from the cleanup queue.",
        });

        onCancelDeletion();
      } else {
        toast({
          title: "Failed to cancel deletion",
          description: axiosError.message || "An unexpected error occurred.",
        });
      }
    } finally {
      setIsLoading(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-primary">
            Cancel deletion of "{organization?.name}"?
          </DialogTitle>
          <DialogDescription>
            This will remove the organization from the cleanup queue. The
            organization and all its data will be preserved, and you will be
            able to update it again.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="items-center justify-between flex w-full">
          <Button
            loading={isLoading}
            onClick={handleCancelDeletion}
            type="submit"
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
